import { chapterContentToPlainText, chapterContentToRichParagraphs } from "./chapterContent";
import type { RichParagraph } from "./tipTapPlainText";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TrimSize = "5x8" | "5.5x8.5" | "6x9" | "8x10" | "8.5x11" | "A5" | "A4";
export type PublishPlatform = "kdp-print" | "ingramspark" | "custom";
export type ChapterHeadingStyle =
  | "title-only"
  | "number-only"
  | "number-and-title";
export type ChapterNumberFormat = "arabic" | "roman" | "written";
export type ChapterStartPage = "any" | "recto";
export type PageNumberPlacement = "none" | "bottom-center" | "bottom-outer" | "bottom-inner";
export type HeaderContent =
  | "none"
  | "book-title"
  | "author-name"
  | "chapter-title"
  | "alternating";
export type TocStyle = "dot-leaders" | "modern" | "minimal";
/** Reading faces — body text only. */
export type BodyFont = "EBGaramond" | "CrimsonText" | "LibreBaskerville" | "LiberationSerif";
/** Body + display faces for chapter headings / ornaments. */
export type PublishFont = BodyFont | "PlayfairDisplay" | "Cinzel";

export type CaptionPlacement = "below-title" | "above-title";
export type CaptionAlign = "center" | "left";
export type HeadingAlign = "left" | "center" | "right";
export type ChapterDecorationLineStyle = "thin" | "medium" | "thick" | "double";

export type ChapterDecorationKind = "text" | "ornament" | "image" | "line";

export type ChapterDecoration = {
  kind: ChapterDecorationKind;
  /** Free-text flourish when kind === "text". */
  text: string;
  /** Preset glyph string when kind === "ornament". */
  ornament: string;
  /** Raster data URL (png/jpeg/webp) when kind === "image". */
  imageDataUrl: string | null;
  /** Natural width/height ratio of the uploaded image (width ÷ height). */
  imageAspect: number | null;
  /** Max image height in pt. */
  maxHeight: number;
  /** Rule weight when kind === "line". */
  lineStyle: ChapterDecorationLineStyle;
};

export const CHAPTER_DECORATION_ORNAMENTS: Array<{ value: string; label: string }> = [
  { value: "* * *", label: "* * *" },
  { value: "• • •", label: "• • •" },
  { value: "✦ ✦ ✦", label: "✦ ✦ ✦" },
  { value: "❧", label: "❧" },
  { value: "◆ ◆ ◆", label: "◆ ◆ ◆" },
  { value: "~~~", label: "~~~" },
  { value: "† † †", label: "† † †" },
  { value: "———", label: "———" },
  { value: "❖ ❖ ❖", label: "❖ ❖ ❖" },
  { value: "✵ ✵ ✵", label: "✵ ✵ ✵" },
];

export const CHAPTER_DECORATION_LINE_STYLES: Array<{
  value: ChapterDecorationLineStyle;
  label: string;
}> = [
  { value: "thin", label: "Thin" },
  { value: "medium", label: "Medium" },
  { value: "thick", label: "Thick" },
  { value: "double", label: "Double" },
];

export function defaultChapterDecoration(): ChapterDecoration {
  return {
    kind: "text",
    text: "* * *",
    ornament: "* * *",
    imageDataUrl: null,
    imageAspect: null,
    maxHeight: 36,
    lineStyle: "thin",
  };
}

export interface PublishSettings {
  platform: PublishPlatform;
  trimSize: TrimSize;
  margins: { top: number; bottom: number; inner: number; outer: number }; // inches
  typography: {
    bodyFont: BodyFont;
    bodyFontSize: number;
    lineSpacing: number;
    paragraphStyle: "indent" | "spacing";
    firstLineIndent: number; // pt
    /** Extra space after each paragraph body (pt). */
    paragraphSpacing: number;
  };
  chapterSettings: {
    headingStyle: ChapterHeadingStyle;
    numberFormat: ChapterNumberFormat;
    chapterStartPage: ChapterStartPage;
    dropCap: boolean;
    headingFont: PublishFont | "same";
    /** Font for text/ornament flourishes (`"same"` → heading family). */
    decorationFont: PublishFont | "same";
    /** Horizontal alignment of chapter number, title, and decoration. */
    headingAlign: HeadingAlign;
    /** Legacy single size — kept for migration; prefer numberFontSize / titleFontSize. */
    headingFontSize: number;
    /** Chapter-number line size (pt). */
    numberFontSize: number;
    /** Chapter-title line size (pt). */
    titleFontSize: number;
    spaceBefore: number; // pt of whitespace above chapter heading
    /** pt of whitespace below heading chrome (title + optional decoration). */
    spaceAfter: number;
    /** When true, draw chapter decoration under the title. */
    showDecoration: boolean;
    decoration: ChapterDecoration;
    /** When true, draw chapter.caption when present. */
    showCaption: boolean;
    captionPlacement: CaptionPlacement;
    captionAlign: CaptionAlign;
    captionItalic: boolean;
    captionFontSize: number;
    captionSpaceBefore: number;
    captionSpaceAfter: number;
  };
  toc: {
    include: boolean;
    title: string;
    style: TocStyle;
  };
  headerFooter: {
    headerContent: HeaderContent;
    pageNumbers: PageNumberPlacement;
    suppressOnChapterFirstPage: boolean;
  };
  frontMatter: {
    halfTitle: boolean;
    titlePage: boolean;
    copyrightPage: boolean;
    copyrightYear: string;
    isbn: string;
    dedicationPage: boolean;
    dedicationText: string;
  };
  backMatter: {
    aboutAuthor: boolean;
    aboutAuthorText: string;
    alsoBy: boolean;
    alsoByText: string;
    blankPageAtEnd: boolean;
  };
  sectionBreakOrnament: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Points = inches × 72
export const TRIM_SIZE_PT: Record<TrimSize, { w: number; h: number }> = {
  "5x8": { w: 360, h: 576 },
  "5.5x8.5": { w: 396, h: 612 },
  "6x9": { w: 432, h: 648 },
  "8x10": { w: 576, h: 720 },
  "8.5x11": { w: 612, h: 792 },
  A5: { w: 420, h: 595 },
  A4: { w: 595, h: 842 },
};

export const TRIM_SIZE_LABELS: Record<TrimSize, string> = {
  "5x8": '5" × 8" (Mass Market)',
  "5.5x8.5": '5.5" × 8.5" (Trade Paper)',
  "6x9": '6" × 9" (Standard Novel)',
  "8x10": '8" × 10" (Large Format)',
  "8.5x11": '8.5" × 11" (Textbook)',
  A5: "A5 (148 × 210mm)",
  A4: "A4 (210 × 297mm)",
};

export const BODY_FONT_LABELS: Record<BodyFont, string> = {
  EBGaramond: "EB Garamond (Classic Serif)",
  CrimsonText: "Crimson Text (Elegant Serif)",
  LibreBaskerville: "Libre Baskerville (Authoritative)",
  LiberationSerif: "Liberation Serif (Universal)",
};

export const PUBLISH_FONT_LABELS: Record<PublishFont, string> = {
  ...BODY_FONT_LABELS,
  PlayfairDisplay: "Playfair Display (Display)",
  Cinzel: "Cinzel (Classical Display)",
};

export const PUBLISH_FONTS: PublishFont[] = [
  "EBGaramond",
  "CrimsonText",
  "LibreBaskerville",
  "LiberationSerif",
  "PlayfairDisplay",
  "Cinzel",
];

export const BODY_FONTS: BodyFont[] = [
  "EBGaramond",
  "CrimsonText",
  "LibreBaskerville",
  "LiberationSerif",
];

export const LINE_SPACING_OPTIONS = [
  { value: 1.0, label: "Single (1.0)" },
  { value: 1.15, label: "Comfortable (1.15)" },
  { value: 1.5, label: "One-and-a-half (1.5)" },
  { value: 1.8, label: "Relaxed (1.8)" },
  { value: 2.0, label: "Double (2.0)" },
];

// ---------------------------------------------------------------------------
// Platform Presets
// ---------------------------------------------------------------------------

const KDP_6X9: PublishSettings = {
  platform: "kdp-print",
  trimSize: "6x9",
  margins: { top: 0.75, bottom: 0.75, inner: 0.875, outer: 0.625 },
  typography: {
    bodyFont: "EBGaramond",
    bodyFontSize: 12,
    lineSpacing: 1.15,
    paragraphStyle: "indent",
    firstLineIndent: 24,
    paragraphSpacing: 0,
  },
  chapterSettings: {
    headingStyle: "number-and-title",
    numberFormat: "arabic",
    chapterStartPage: "recto",
    dropCap: false,
    headingFont: "same",
    decorationFont: "same",
    headingAlign: "center",
    headingFontSize: 22,
    numberFontSize: 22,
    titleFontSize: 16,
    spaceBefore: 72,
    spaceAfter: 20,
    showDecoration: false,
    decoration: defaultChapterDecoration(),
    showCaption: true,
    captionPlacement: "below-title",
    captionAlign: "center",
    captionItalic: true,
    captionFontSize: 11,
    captionSpaceBefore: 8,
    captionSpaceAfter: 16,
  },
  toc: { include: true, title: "Contents", style: "dot-leaders" },
  headerFooter: {
    headerContent: "alternating",
    pageNumbers: "bottom-outer",
    suppressOnChapterFirstPage: true,
  },
  frontMatter: {
    halfTitle: true,
    titlePage: true,
    copyrightPage: true,
    copyrightYear: String(new Date().getFullYear()),
    isbn: "",
    dedicationPage: false,
    dedicationText: "",
  },
  backMatter: {
    aboutAuthor: true,
    aboutAuthorText: "",
    alsoBy: false,
    alsoByText: "",
    blankPageAtEnd: true,
  },
  sectionBreakOrnament: "* * *",
};

export const PLATFORM_PRESETS: Record<"kdp-print" | "ingramspark", PublishSettings> = {
  "kdp-print": KDP_6X9,
  ingramspark: {
    ...KDP_6X9,
    platform: "ingramspark",
    margins: { top: 0.75, bottom: 0.875, inner: 1.0, outer: 0.75 },
    typography: {
      ...KDP_6X9.typography,
      bodyFont: "LibreBaskerville",
      bodyFontSize: 11,
    },
    chapterSettings: {
      ...KDP_6X9.chapterSettings,
      headingFontSize: 20,
      numberFontSize: 20,
      titleFontSize: 14,
    },
  },
};

export function defaultPublishSettings(): PublishSettings {
  return { ...KDP_6X9, frontMatter: { ...KDP_6X9.frontMatter, copyrightYear: String(new Date().getFullYear()) } };
}

// ---------------------------------------------------------------------------
// Page estimation (for UI preview)
// ---------------------------------------------------------------------------

export const HEADER_RESERVE_PT = 24; // pt reserved above content for header
export const FOOTER_RESERVE_PT = 24; // pt reserved below content for footer

export function estimateTotalPages(
  book: { chapters?: Array<{ content?: string }> },
  settings: PublishSettings
): number {
  let pages = countFrontMatterPages(settings);
  const chapterPages = (book.chapters ?? []).map((ch) =>
    estimateChapterPages(ch, settings)
  );
  if (settings.toc.include) pages += Math.max(1, Math.ceil((book.chapters?.length ?? 0) / 20));
  for (const cp of chapterPages) pages += cp;
  if (settings.backMatter.aboutAuthor) pages += 1;
  if (settings.backMatter.alsoBy) pages += 1;
  if (settings.backMatter.blankPageAtEnd) pages += 1;
  return pages;
}

export function countFrontMatterPages(settings: PublishSettings): number {
  let n = 0;
  if (settings.frontMatter.halfTitle) n++;
  if (settings.frontMatter.titlePage) n++;
  if (settings.frontMatter.copyrightPage) n++;
  if (settings.frontMatter.dedicationPage) n++;
  return n;
}

export function estimateChapterPages(
  chapter: { content?: string },
  settings: PublishSettings
): number {
  const text = resolveChapterText(chapter);
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  if (!wordCount) return 1;

  const { w, h } = TRIM_SIZE_PT[settings.trimSize];
  const { top, bottom, inner, outer } = settings.margins;
  const hasHeader = settings.headerFooter.headerContent !== "none";
  const hasFooter = settings.headerFooter.pageNumbers !== "none";
  const textW = w - (inner + outer) * 72;
  const textH =
    h -
    (top + bottom) * 72 -
    (hasHeader ? HEADER_RESERVE_PT : 0) -
    (hasFooter ? FOOTER_RESERVE_PT : 0);

  const { bodyFontSize, lineSpacing } = settings.typography;
  const charsPerLine = Math.floor(textW / (bodyFontSize * 0.52));
  const wordsPerLine = charsPerLine / 5.8;
  const linesPerPage = Math.floor(textH / (bodyFontSize * lineSpacing));
  const wordsPerPage = Math.max(1, wordsPerLine * linesPerPage);

  return Math.max(1, Math.ceil(wordCount / wordsPerPage));
}

export function resolveChapterText(chapter: { content?: unknown }): string {
  return chapterContentToPlainText(chapter.content ?? "");
}

export function resolveChapterRichParagraphs(chapter: { content?: unknown }): RichParagraph[] {
  return chapterContentToRichParagraphs(chapter.content ?? "");
}
